// Colby College: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Colby posts no Common Data Set: the old Institutional Research "dataset"
//    address now opens the office's directory page, and neither that office's
//    pages (Consumer Information, External Surveys, Assessment, Policies) nor
//    colby.edu's page sitemaps link a CDS of any year. The first-year counts
//    come from the U.S. Department of Education's IPEDS Admissions survey for
//    Fall 2024, first-time degree-seeking students (College Navigator and the
//    ADM2024 complete data file, unit 161086). IPEDS gives no residency split
//    and no early decision counts, so this page has neither.
//  - Colby Admissions and Financial Aid (afa.colby.edu): College Profile (the
//    Class of 2030 applicant count and admit rate), Requirements, Dates and
//    Deadlines, FAQ (test-optional policy, Early Decision), and Tuition and
//    Fees (the 2026-2027 estimated cost of attendance).
//  - College Scorecard API, school id 161086 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=161086#admsns',
        read: '2026-10-01',
    },
    collegeProfile: {
        label: 'Colby Admissions, College Profile',
        url: 'https://afa.colby.edu/apply/college-profile/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Colby Admissions, Requirements',
        url: 'https://afa.colby.edu/apply/requirements/',
        read: '2026-10-01',
    },
    dates: {
        label: 'Colby Admissions, Dates and Deadlines',
        url: 'https://afa.colby.edu/apply/dates-and-deadlines/',
        read: '2026-10-01',
    },
    faq: {
        label: 'Colby Admissions, FAQ',
        url: 'https://afa.colby.edu/apply/faq/',
        read: '2026-10-01',
    },
    tuition: {
        label: 'Colby Admissions and Financial Aid, 2026-2027 Tuition and Fees',
        url: 'https://afa.colby.edu/cost-and-aid/tuition-and-fees/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?161086-Colby-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'colby-college',
    kbId: 'colby_college',
    name: 'Colby College',
    shortName: 'Colby',
    location: 'Waterville, ME',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Colby identity style guide); decoration only.
    branding: {
        primary: '#002878',
        secondary: '#E3D7D1',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/colby_college_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 19187,
        admitted: 1360,
        enrolled: 628,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Colby does not post a Common Data Set, so the counts above come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole college and none by major. Colby\'s own College Profile gives 21,065 applicants and 8 percent admitted for the Class of 2030, which entered in fall 2026 (as of August 1, 2026), without an admitted count.',
        source: 'collegeProfile',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 15', decision: 'On or before December 15', binding: true },
            { plan: 'Early Decision II', deadline: 'January 4', decision: 'On or before February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'On or before April 1', binding: false },
        ],
        note: 'Optional and supporting materials are due November 22 for Early Decision I and January 15 for Early Decision II and Regular Decision. The financial aid deadline (CSS Profile, FAFSA and 2025 tax returns) is November 15 for Early Decision I, January 4 for Early Decision II and January 15 for Regular Decision. If you applied Regular Decision before November 15 or January 4, you can switch to Early Decision by sending a signed Early Decision Agreement by December 1 (Early Decision I) or February 1 (Early Decision II).',
        source: 'dates',
        binding: 'Early Decision is binding: you submit the Early Decision Agreement, and if you are admitted you must withdraw all other applications immediately and may not start new ones. Colby has no early action plan.',
        bindingSource: 'faq',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Colby is test-optional: you choose whether to send SAT or ACT scores. If you do not, you tell Colby by email or through the Standardized Test Score Update form in your admissions portal. If you send scores you may report them yourself, and Colby superscores all exams, including the ACT.',
        source: 'faq',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'requirements',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition and fees', amount: 75790 },
            { item: 'Food and housing', amount: 19480 },
            { item: 'Personal and books', amount: 1700 },
            { item: 'Travel and transportation', amount: null },
        ],
        total: 96970,
        note: 'Travel runs $50 to $1,300 depending on your home address, so it is not in the $96,970 here; Colby gives the estimated cost of attendance as $97,020 to $98,270. Incoming first-year students are also billed a $500 fee for the Colby Outdoor Orientation Trips.',
        source: 'tuition',
        netPrice: {
            average: 19835,
            byIncome: [
                { income: '$0 to $30,000', amount: 5263 },
                { income: '$30,001 to $48,000', amount: 2358 },
                { income: '$48,001 to $75,000', amount: 3132 },
                { income: '$75,001 to $110,000', amount: 10750 },
                { income: 'Over $110,000', amount: 53340 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Colby\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
